import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  approveChangelogDigest,
  composeChangelogDigest,
  deleteChangelogDigest,
  regenerateChangelogDigest,
  revokeChangelogDigestApproval,
  saveChangelogDigest,
  sendChangelogDigest,
  type ChangelogChannel,
  type ChangelogDigest,
  type ChangelogDigestEditInput,
  type ChangelogDigestPage,
} from "@/features/changelog/api/changelog.api";
import { changelogKeys } from "@/lib/queries";

/**
 * Every write in this tab reports its own failure next to the control that
 * caused it, so the global error toast would be a second, less useful copy.
 */
const LOCAL_ERRORS = { meta: { skipGlobalError: true } } as const;

/**
 * A write returns the roundup it changed, so the list is patched in place
 * rather than refetched — the card keeps its scroll position and the approve
 * button flips the moment the server agrees.
 */
function useDigestWriter() {
  const queryClient = useQueryClient();

  return (digest: ChangelogDigest) => {
    queryClient.setQueryData<ChangelogDigestPage>(changelogKeys.digests, (page) =>
      page
        ? { ...page, digests: page.digests.map((d) => (d.id === digest.id ? digest : d)) }
        : page,
    );
  };
}

export function useComposeChangelogDigest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: composeChangelogDigest,
    // Composing both adds a roundup and removes its deploys from the candidate
    // pool, so neither list can be patched from the reply alone.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: changelogKeys.digests });
      await queryClient.invalidateQueries({ queryKey: changelogKeys.candidates });
    },
    ...LOCAL_ERRORS,
  });
}

export function useRegenerateChangelogDigest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: regenerateChangelogDigest,
    // The merge answers before the worker starts, so refetching is what picks
    // up the GENERATING status that starts the poll.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: changelogKeys.digests }),
    ...LOCAL_ERRORS,
  });
}

export function useSaveChangelogDigest() {
  const writeDigest = useDigestWriter();

  return useMutation({
    mutationFn: (input: ChangelogDigestEditInput) => saveChangelogDigest(input),
    onSuccess: writeDigest,
    ...LOCAL_ERRORS,
  });
}

export function useApproveChangelogDigest() {
  const writeDigest = useDigestWriter();

  return useMutation({
    mutationFn: ({
      digestId,
      expectedUpdatedAt,
    }: {
      digestId: string;
      expectedUpdatedAt: string;
    }) => approveChangelogDigest(digestId, expectedUpdatedAt),
    onSuccess: writeDigest,
    ...LOCAL_ERRORS,
  });
}

export function useRevokeChangelogDigestApproval() {
  const writeDigest = useDigestWriter();

  return useMutation({
    mutationFn: (digestId: string) => revokeChangelogDigestApproval(digestId),
    onSuccess: writeDigest,
    ...LOCAL_ERRORS,
  });
}

export function useSendChangelogDigest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      digestId,
      channels,
      force,
    }: {
      digestId: string;
      channels: ChangelogChannel[];
      force?: boolean;
    }) => sendChangelogDigest(digestId, channels, force),
    // Delivery finishes in a worker, so the reply's poll hint has to reach the
    // page level: patching the roundup alone would leave the tab showing
    // "sending" with nothing scheduled to find out that it finished.
    onSuccess: ({ pollAfterMs, ...digest }) => {
      queryClient.setQueryData<ChangelogDigestPage>(changelogKeys.digests, (page) =>
        page
          ? {
              ...page,
              pollAfterMs: pollAfterMs ?? page.pollAfterMs,
              digests: page.digests.map((d) => (d.id === digest.id ? digest : d)),
            }
          : page,
      );
    },
    ...LOCAL_ERRORS,
  });
}

export function useDeleteChangelogDigest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteChangelogDigest,
    // Discarding releases the roundup's deploys back to Compose, so both lists
    // are stale afterwards.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: changelogKeys.digests });
      await queryClient.invalidateQueries({ queryKey: changelogKeys.candidates });
    },
    ...LOCAL_ERRORS,
  });
}
